/**
 * Text banks and combinatorial patterns for Divergent timeline narrative generation.
 * Recognisable human-scale reality in our physics where one early decision went the other way.
 * Covers diverse climates, modern/historical-adjacent eras, global regions, and real professions.
 */

import type { InterestId, LifePathId, AmbitionId } from "../types";

// ==========================================
// 1. Titles / Epithets (built from role + place/object/theme)
// ==========================================
export const TITLE_PREFIXES = [
  "Steward of the",
  "Surveyor of the",
  "Chronicler of the",
  "Curator of the",
  "Restorer of the",
  "Warden of the",
  "Archivist of",
  "Apprentice of the",
  "Keeper of the",
  "Builder of the",
  "Mediator of the",
  "Cartographer of the",
  "Registrar of the",
  "Inspector of the",
  "Navigator of the",
] as const;

export const TITLE_OBJECTS = [
  "Lowland Estuary",
  "Forgotten Canal Network",
  "High Ridge Weather Station",
  "Provincial Seed Vault",
  "Terrace Irrigation Works",
  "Old Municipal Foundry",
  "Harbor Signal Towers",
  "Valley Timber Cooperative",
  "Highland Botanical Nursery",
  "Border Railway Junction",
  "Desert Hydrology Project",
  "Coastal Shingle Bank",
  "River Basin Settlement",
  "Urban Rooftop Apiaries",
  "Archival Audio Cylinders",
  "Island Ferry Link",
  "Quarry Community Guild",
  "Alpine Meadow Reserves",
] as const;

// ==========================================
// 2. Taglines (assembled from 2 parts: >= 40 combinations)
// ==========================================
export const TAGLINE_PART_A = [
  "A life built on the road you quietly turned down at twenty-two,",
  "Rooted in the very regional city you thought you would leave before autumn,",
  "Trading the metropolitan rush for the deliberate cadence of stone and water,",
  "Where patience was chosen over urgency, and local craft over public spectacle,",
  "A grounded timeline anchored by an unexpected conversation on a rain-swept platform,",
  "Finding immense gravity in small, recurring daily obligations,",
  "The quiet fulfillment of a promise made across a family dining table years ago,",
  "Proof that staying put can be its own demanding, courageous exploration,",
  "Shaped by the solitary evening you tore up a plane ticket to stay beside family,",
  "A path of deliberate friction and honest materials instead of abstract corporate ladders,",
] as const;

export const TAGLINE_PART_B = [
  "learning that mastering one valley is deeper than skimming a continent.",
  "finding enduring wonder in the rhythm of shifting tides and neighborhood ledgers.",
  "building a trade whose marks will outlive transient market cycles.",
  "where every street corner holds the layered history of three generations.",
  "earning civic trust that no credential or metropolitan title could ever buy.",
  "surrendering easy prestige to safeguard the people who raised you.",
  "content that real consequence lives in physical things well made and faithfully kept.",
  "anchored by a community that remembers your childhood mistakes and forgives them daily.",
] as const;

// ==========================================
// 3. Occupations: Domain (Interest) x Life Path Setting/Seniority
// >= 6 role variants per interest, >= 4 modifier variants per life path
// ==========================================
export const OCCUPATION_ROLES: Record<InterestId, readonly string[]> = {
  science: [
    "Soil Hydrologist",
    "Field Glaciologist",
    "Microbial Ecologist",
    "Observatory Technician",
    "Atmospheric Data Analyst",
    "Aquifer Restoration Specialist",
    "Seismological Station Monitor",
    "Agricultural Agronomist",
  ],
  art: [
    "Architectural Drafter",
    "Typographic Bookbinder",
    "Landscape Painter & Pigment Maker",
    "Stained Glass Conservator",
    "Ceramic Tile Mason",
    "Exhibition Scenographer",
    "Textile Weaver & Natural Dyer",
    "Heirloom Furniture Sculptor",
  ],
  music: [
    "Acoustic Instrument Luthier",
    "Choral Music Director",
    "Broadcast Sound Recordist",
    "Orchestral Percussion Restorer",
    "Traditional Ballad Ethnomusicologist",
    "Concert Hall Acoustician",
    "Folk String Instructor",
    "Pipe Organ Voicer",
  ],
  nature: [
    "Watershed Habitat Ranger",
    "Heirloom Fruit Orchardist",
    "Alpine Forestry Steward",
    "Wetland Rehabilitation Lead",
    "Botanical Seed Bank Curator",
    "Urban Agroecology Coordinator",
    "Coastal Marine Biologist",
    "Wildlife Corridor Tracker",
  ],
  technology: [
    "Precision Machinist",
    "Municipal Grid Relay Engineer",
    "Mechanical Clockwork Horologist",
    "Solar Microgrid Installer",
    "Locomotive Diesel Specialist",
    "Hydraulic Pump Fabricator",
    "Bridge Structural Inspector",
    "Telemetry Network Technician",
  ],
  people: [
    "Rural Clinic Coordinator",
    "Community Mediation Counselor",
    "Vocational Apprenticeship Director",
    "Municipal Housing Advocate",
    "Elder Care Cooperative Organizer",
    "Public Library Operations Lead",
    "Youth Wilderness Instructor",
    "Civic Neighborhood Liaison",
  ],
  stories: [
    "Regional Oral Historian",
    "Letterpress Memoir Publisher",
    "Antiquarian Bookseller",
    "Township Archive Registrar",
    "Community Theater Dramaturg",
    "Cultural Heritage Documentarian",
    "Independent Investigative Reporter",
    "Folkloric Audio Archivist",
  ],
  adventure: [
    "Backcountry Trail Crew Leader",
    "Coastal Ferry Navigation Officer",
    "High-Altitude Weather Lookout",
    "Search and Rescue Field Scout",
    "River Canyon Kayak Guide",
    "Cartographic Survey Surveyor",
    "Offshore Lightstation Tender",
    "Winter Avalanche Safety Specialist",
  ],
};

export const OCCUPATION_MODIFIERS: Record<LifePathId, readonly string[]> = {
  rooted: [
    "Regional",
    "County",
    "Township",
    "District",
    "Heritage",
    "Valley",
  ],
  scholar: [
    "Research",
    "Senior",
    "Archival",
    "Institute",
    "Analytical",
    "Field Faculty",
  ],
  maker: [
    "Master",
    "Independent",
    "Artisanal",
    "Custom Fabricating",
    "Bench",
    "Guild",
  ],
  voyager: [
    "Itinerant",
    "Perimeter",
    "Expeditionary",
    "Transit",
    "Mobile",
    "Cross-Border",
  ],
  guide: [
    "Community",
    "Lead Consulting",
    "Civic",
    "Supervising",
    "Mentor",
    "Cooperative",
  ],
  founder: [
    "Founding Director of the",
    "Lead Organizer of the",
    "Principal Partner at the",
    "Chief Coordinator for the",
  ],
};

// ==========================================
// 4. Formative Early Fork Beats (Ambition x LifePath x Fork Type)
// >= 10 fork types with concrete specifics
// ==========================================
export interface EarlyFork {
  category: string;
  template: string;
}

export const FORK_TEMPLATES: readonly EarlyFork[] = [
  {
    category: "declined_move",
    template: "When a prestigious relocation contract to {distantCity} arrived with an all-expenses stipend, {name} spent a night walking along {localFeature}, finally shredding the acceptance letter to stay beside family.",
  },
  {
    category: "different_degree",
    template: "Having passed the entrance examination for international finance in {distantCity}, {name} quietly withdrew tuition funds to enroll in a provincial trade academy specializing in {domainPractice}.",
  },
  {
    category: "stayed_for_relative",
    template: "At twenty-one, when an overseas research expedition offered an open bunk, {name} remained home to nurse an ailing uncle through winter, discovering an unexpected dedication to the local {domainSetting}.",
  },
  {
    category: "left_firm",
    template: "Two years into a lucrative corporate consultancy in {distantCity}, {name} packed a single duffel bag, handed in their notice, and returned to the quiet workshop near {localFeature}.",
  },
  {
    category: "risky_cooperative",
    template: "Rather than signing on as a salaried inspector with a state conglomerate, {name} pooled modest savings with three classmates to found an independent cooperative dedicated to {domainPractice}.",
  },
  {
    category: "turn_down_fellowship",
    template: "Offered a five-year academic tenure track in {distantCity}, {name} declined the chair, convinced that true understanding of {domainNoun} requires daily field immersion rather than seminar theory.",
  },
  {
    category: "stand_during_strike",
    template: "During the contentious labor dispute at the regional docks, {name} walked off a management training path to run the supply canteen for striking workers, forever cementing local loyalties.",
  },
  {
    category: "refused_buyout",
    template: "When property developers offered a staggering sum to clear the ancestral parcel near {localFeature}, {name} refused the check, committed to preserving the working {domainSetting}.",
  },
  {
    category: "community_election",
    template: "Drafted unexpectedly into a contested election for the municipal council at twenty-six, {name} gave up a comfortable drafting desk to manage the township's crumbling water infrastructure.",
  },
  {
    category: "interrupted_voyage",
    template: "At the midpoint of a round-the-world freighter voyage, {name} disembarked at {localPlace}, struck by the quiet dignity of the working waterfront, and never returned to the ship.",
  },
  {
    category: "apprentice_oath",
    template: "While peers departed on high-speed rail for metropolitan technology hubs, {name} signed an eight-year apprenticeship bound to the oldest master of {domainPractice} in the province.",
  },
  {
    category: "emergency_stewardship",
    template: "When an unseasonal storm destroyed the valley's primary bridge, {name} abandoned travel visas to help rebuild the abutments, realizing their hands belonged to this riverbed.",
  },
];

// ==========================================
// 5. Story Parts: Origin (3 parts x >= 12 variants)
// ==========================================
export const ORIGIN_CHILDHOOD = [
  "Raised in a drafty timber house on the outskirts of {homeRegion},",
  "Growing up within earshot of the freight rail shunting yard in {homeRegion},",
  "In a modest brick terrace overlooking the tidal flats of {homeRegion},",
  "Surrounded by cedar-wood worktables and weathered ledger books in {homeRegion},",
  "Early childhood in a hillside stone farmhouse above {homeRegion} was marked by long winters and meticulous chores,",
  "Born to a family of dockside freight handlers along the bustling quay of {homeRegion},",
  "In the rain-shadow valleys of {homeRegion}, where seasonal fog lingers well into midday,",
  "Spending summer afternoons beneath the rusted corrugated roofs of the railway repair shed in {homeRegion},",
  "Growing up in an apartment over a bustling municipal bakery in {homeRegion},",
  "Raised among the terraced orchard rows that climb the limestone bluffs of {homeRegion},",
  "In a drafty second-story flat overlooking the municipal canal locks of {homeRegion},",
  "Between dry-goods warehouses and narrow cobblestone alleys in old {homeRegion},",
] as const;

export const ORIGIN_CURIOSITY = [
  "{name} learned early that every broken valve or worn hand-plane told an intimate history of human effort.",
  "{name} developed an instinct for listening to how timber creaks and metal expands under seasonal shifts.",
  "{name} filled pocket notebooks with hand-drawn maps of drainage ditches, powerlines, and alleyway shortcuts.",
  "{name} observed that neighbors rarely remembered grand political speeches, but never forgot who fixed the communal boiler.",
  "{name} acquired a quiet fascination for {interestField}, cataloging discarded components with scientific reverence.",
  "{name} watched older relatives navigate municipal disputes through stubborn patience rather than raised voices.",
  "{name} spent dawn hours recording water levels, bird migrations, and barometric trends in pencil.",
  "{name} learned to weigh practical utility against superficial novelty, favoring tools that lasted forty years.",
  "{name} mastered the discipline of measuring twice and cutting once, whether drafting floor plans or phrasing advice.",
  "{name} came to believe that true competence is quiet, habitual, and unconcerned with theatrical praise.",
  "{name} found endless intrigue in the intersection of {interestNoun} and everyday neighborhood survival.",
  "{name} discovered that keeping promises across kitchen tables built more lasting security than any paper bond.",
] as const;

export const ORIGIN_FORMATION = [
  "By eighteen, mentors already recognized an uncommon blend of {strengthAdj} focus and physical stamina.",
  "Early employment at the district depot taught {name} how fragile supply chains really are when weather turns severe.",
  "A secondary school teacher provided worn classical textbooks, igniting a lifelong habit of {strengthHabit}.",
  "Assisting in the family trade instilled an enduring respect for physical craft over bureaucratic theory.",
  "Long solitary bicycle rides across the province cemented an abiding intimacy with the regional watershed.",
  "Working nights at the telephone exchange gave {name} a panoramic understanding of the town's social fabric.",
  "An elderly bookbinder gifted {name} three volumes of natural history, permanently orienting their mind toward systematic care.",
  "Surviving a harsh regional freeze alongside neighbors demonstrated the life-or-death necessity of mutual aid.",
  "An uncle's mechanical workshop served as an informal laboratory for testing theories against greasy steel.",
  "Local elders frequently entrusted {name} with confidential community errands, trusting their discreet judgement.",
  "Quiet hours spent reading in the municipal library cultivated an appetite for first-principles reasoning.",
  "Early exposure to trade disputes taught {name} that most human quarrels stem from unacknowledged fear of scarcity.",
] as const;

// ==========================================
// 6. Story Parts: Turning Point (3 parts x >= 12 variants)
// ==========================================
export const TURNING_CRISIS = [
  "When the decisive fork arrived in {name}'s early twenties,",
  "The pivot occurred during a bitter autumn marked by factory reorganizations across the province,",
  "A crossroads emerged when metropolitan recruiters swept through town offering unprecedented bonuses,",
  "The defining trial materialized during the severe drought of that memorable summer,",
  "At twenty-three, an official letter bearing the embossed seal of a distant ministry forced a decision,",
  "Everything shifted the evening an unexpected telegram summoned {name} to an urgent family meeting,",
  "When an international consortium announced plans to buy out the regional water authority,",
  "The moment of reckoning arrived on a rainy November Tuesday at the central railway terminal,",
  "During the financial downturn that shuttered three of the county's major employer works,",
  "When a mentor's unexpected illness left the district workshop without clear leadership,",
  "At the threshold of what should have been a standard relocation to a corporate campus,",
  "When an offer to join an exploratory oceanographic crew came with a four-year binding overseas contract,",
] as const;

export const TURNING_CHOICE = [
  "{name} chose to turn back toward {homePlace}, declining the easy prestige of a metropolitan title.",
  "{name} deliberately tore up the relocation papers, committing their labor to the local community instead.",
  "{name} refused the buyout contract, resolving to anchor their livelihood in tangible local relationships.",
  "{name} stepped forward to shoulder the debt and daily operations of the struggling workshop.",
  "{name} walked away from an air-conditioned office career to apprentice on the muddy banks of the canal.",
  "{name} chose the arduous path of an independent guild practitioner rather than becoming an employee number.",
  "{name} accepted the modest chairmanship of the district cooperative, knowing the salary was third of the corporate offer.",
  "{name} resolved that five hundred neighbors known by their first names held more human reality than ten thousand anonymous users.",
  "{name} packed their work clothes, took the local stopping train back home, and unpacked tools at the ancestral bench.",
  "{name} declined an overseas fellowship, deciding that studying one river basin in absolute depth was their true life work.",
  "{name} stood alone at the civic council meeting to vote against the speculative development petition.",
  "{name} chose the messy, hands-on labor of restoration over the sanitized detachment of distant consulting.",
] as const;

export const TURNING_AFTERMATH = [
  "The decision stunned former classmates, but {name} never spent a night regretting the quiet gravity of that choice.",
  "Though early winters brought financial strain and blistered hands, the foundation for genuine mastery was laid.",
  "It cemented a reputation for stubborn integrity that would shield {name} through the unpredictable decades ahead.",
  "The sacrifice was real, yet it unlocked an authentic, grounded dignity that high-rise offices never provide.",
  "Neighbors took notice of the young technician who chose to stay, gradually entrusting {name} with critical civic responsibilities.",
  "Within three years, the workshop was not merely solvent, but functioning as the social and technical heartbeat of the ward.",
  "It established the central rule of {name}'s life: never trade enduring local affection for temporary distant acclaim.",
  "That single evening re-calibrated their entire relationship to time, ambition, and community service.",
  "Old friends who migrated to the capital often return for advice, envying the profound rootedness {name} quietly carved out.",
  "It forged an unbreakable partnership with local craftspeople that continues to shape provincial policy.",
  "From that winter onward, every tool {name} lifted carried the moral conviction of a life deliberately chosen.",
  "The road not taken remained a phantom; the road chosen became a rich, populated world of iron, wood, and trust.",
] as const;

// ==========================================
// 7. Story Parts: Today Beats (3 parts x >= 12 variants)
// ==========================================
export const TODAY_DAWN = [
  "Today, {name} wakes before daybreak while mist still blankets {homePlace},",
  "Now well established in {homePlace}, {name}'s mornings begin with strong black tea and a review of the meteorological logbook,",
  "At sunrise over the working docks of {homePlace}, {name} is already at the bench,",
  "Morning light filters through dusty clerestory windows into {name}'s workshop in {homePlace},",
  "Life has settled into an unhurried, rhythmic cadence along the quiet streets of {homePlace},",
  "Stepping onto the porch of the timber cottage near {homePlace} at first light,",
  "Before the district telephone lines start ringing, {name} inspects the overnight readings at the {homePlace} station,",
  "Dawn over {homePlace} finds {name} walking the towpath, notebook tucked underarm,",
  "In the quiet hours before the apprentices arrive at the {homePlace} facility,",
  "Awakening to the rhythmic clang of the morning freight train through {homePlace},",
  "With coffee in a chipped enamel mug, {name} looks out over the terraced hills of {homePlace},",
  "Early daylight catches the brass vernier scales and worn walnut straightedges on {name}'s drafting desk in {homePlace},",
] as const;

export const TODAY_WORK = [
  "where daily labor involves {domainVerb} with instruments maintained by their own hands for over twenty years.",
  "coordinating complex restoration tasks alongside colleagues who have shared this trade for two generations.",
  "mentoring a new cohort of young technicians who seek refuge from the disposable pace of modern digital work.",
  "fielding continuous inquiries from regional farmers, municipal engineers, and neighbors needing steady advice.",
  "balancing demanding technical calculations with the tactile, forgiving resistance of physical materials.",
  "reviewing hand-drawn survey charts that record every subtle seasonal change across thirty square miles.",
  "mediating disagreements between water boards and local craftspeople with calm, dispassionate fairness.",
  "troubleshooting mechanical faults that automated diagnostic systems repeatedly misclassify.",
  "hand-binding reports and drafting restorative blueprints that preserve the town's historical identity.",
  "ensuring that critical civic equipment runs reliably through severe storms without relying on external subsidies.",
  "practicing an exacting standard of craft where a millimeter of tolerance represents personal dishonor.",
  "tending to the living systems that sustain the valley, knowing every creek by its seasonal sound.",
] as const;

export const TODAY_EVENING = [
  "Evenings are spent around the kitchen table with family and visiting apprentices, sharing bread, cider, and dry laughter.",
  "As dusk turns indigo, {name} walks down to the harbor tavern to hear gossip of an outside world that never managed to lure them away.",
  "Sitting by the iron stove with a ledger and classical radio, {name} smiles at old doubts, entirely at peace with the boundary stones of this life.",
  "The hands are weathered and permanently stained with oil and tannin, but they move with the quiet grace of total alignment.",
  "A light burns late in the upper study window—a familiar beacon for neighbors crossing the pass in foul weather.",
  "There are no national prizes or television features, yet three hundred households sleep safer because {name} stays on watch.",
  "Looking across the valley as chimney smoke rises into the autumn dusk, {name} feels the deep, unshakeable weight of home.",
  "When old companions write from metropolitan towers, {name} answers warmly, feeling neither envy nor superiority—only gratitude.",
  "Bedtime brings the profound, dreamless sleep of someone whose muscles and conscience were equally exercised.",
  "The community respects {name} not as a celebrity, but as the steady hand that never wavered when the winter river flooded.",
  "Closing the workshop shutters for the night, {name} touches the worn latch, thankful for the road taken thirty years ago.",
  "At peace with the modest acreage of this chosen reality, {name} knows that living deeply in one place touches every universe.",
] as const;

// ==========================================
// 8. Choices: 3 Distinct Kinds (Loss, Opportunity, Conviction)
// ==========================================
export interface ChoiceTriplet {
  loss: { age: string; choice: string; roadNotTaken: string; consequence: string };
  opportunity: { age: string; choice: string; roadNotTaken: string; consequence: string };
  conviction: { age: string; choice: string; roadNotTaken: string; consequence: string };
}

export const CHOICE_BANK_LOSS = [
  {
    age: "Age 20",
    choice: "Declined the overseas university scholarship to stay home when family finances collapsed after the mill closure.",
    roadNotTaken: "Accepting a full tuition waiver at the metropolitan technical institute.",
    consequence: "Mastered the practical trade through gritty improvisation, though gave up formal academic titles.",
  },
  {
    age: "Age 22",
    choice: "Remained behind when a first love boarded the westbound sleeper train for a finance career in the capital.",
    roadNotTaken: "Moving to an unfamiliar metropolis on a romantic gamble with no independent job.",
    consequence: "Carried a quiet ache for several seasons, but established deep, permanent roots in the home valley.",
  },
  {
    age: "Age 24",
    choice: "Gave up an elite research fellowship in maritime biology to nurse an injured mentor through two critical years.",
    roadNotTaken: "Publishing early peer-reviewed papers on coral systems abroad.",
    consequence: "Inherited the mentor's irreplaceable forty-year regional archive and complete trust of the local fleet.",
  },
  {
    age: "Age 21",
    choice: "Withdrew from an international athletics team after an ankle fracture, refocusing all energy on civil apprenticeship.",
    roadNotTaken: "Enduring surgery and months of uncertain physical therapy chasing podium glory.",
    consequence: "Discovered an intellectual discipline that outlasted youthful athletic stamina by forty years.",
  },
] as const;

export const CHOICE_BANK_OPPORTUNITY = [
  {
    age: "Age 28",
    choice: "Purchased the bankrupt municipal foundry at auction using high-interest personal loans and cooperative shares.",
    roadNotTaken: "Letting corporate liquidators scrap the historic machinery for overseas metal recycling.",
    consequence: "Preserved sixteen skilled manufacturing jobs and created an open technical school for district youth.",
  },
  {
    age: "Age 29",
    choice: "Took over the derelict hillside orchard during the year of the blight, replanting with resilient heirloom rootstock.",
    roadNotTaken: "Allowing the parcel to be rezoned for a high-density suburban tract housing development.",
    consequence: "Revived a three-hundred-year agricultural lineage that now supplies sixty regional cooperative kitchens.",
  },
  {
    age: "Age 31",
    choice: "Launched an independent regional hydrological survey after state agencies defunded the provincial weather stations.",
    roadNotTaken: "Relying on generic satellite models that missed localized microclimate flood risks.",
    consequence: "Provided the flood warning that saved three lowland villages from catastrophic flash inundation.",
  },
  {
    age: "Age 30",
    choice: "Converted an abandoned grain silo into the district's first shared letterpress studio and oral history repository.",
    roadNotTaken: "Accepting a comfortable marketing directorship for a commercial retail chain.",
    consequence: "Created a beloved community landmark that has preserved over four hundred personal audio histories.",
  },
] as const;

export const CHOICE_BANK_CONVICTION = [
  {
    age: "Age 35",
    choice: "Publicly testified against a corrupt regional dam project, refusing a lucrative engineering consulting retainer.",
    roadNotTaken: "Remaining quietly neutral to protect future government contracts.",
    consequence: "Faced three years of spiteful administrative audits, but protected the valley's drinking aquifer permanently.",
  },
  {
    age: "Age 36",
    choice: "Mediated the contentious five-year water rights dispute between mountain farms and lowland paper mills without charging fees.",
    roadNotTaken: "Taking sides with the wealthier industrial lobby to secure long-term workshop patronage.",
    consequence: "Earned universal civic moral authority across the region at the cost of substantial billable hours.",
  },
  {
    age: "Age 38",
    choice: "Refused to sign off on an unsafe municipal highway shortcut that would have bisected ancestral forest wetlands.",
    roadNotTaken: "Rubber-stamping the environmental assessment to stay on good terms with county politicians.",
    consequence: "Endured temporary political isolation, but the route was redirected and the heron habitat survived.",
  },
  {
    age: "Age 34",
    choice: "Kept the community cooperative warehouse open through the brutal winter freeze, distributing fuel reserves on trust.",
    roadNotTaken: "Locking the facility doors to safeguard inventory until cash payments could be guaranteed.",
    consequence: "Prevented forty families from freezing; every single borrower paid back their debt by the following spring.",
  },
] as const;

// ==========================================
// 9. World Details: Extra Details Pool (>= 12 variants)
// ==========================================
export const DIVERGENT_WORLD_EXTRAS = [
  { label: "Community Core", value: "A tight network of sixty households trading carpentry, welding, seeds, and medical aid." },
  { label: "Technology Baseline", value: "Analog hydraulic gauges, diesel generators, hand tools, and copper-wire telephone lines." },
  { label: "Key Artifact", value: "A thumbed, oil-stained pocket notebook containing twenty-four years of seasonal river logs." },
  { label: "Civic Governance", value: "A monthly town hall where disputes are settled by consensus under an old chestnut tree." },
  { label: "Architectural Style", value: "Slate roofs, heavy mortise-and-tenon timber framing, and thick river-stone foundations." },
  { label: "Energy Source", value: "A refurbished 1930s run-of-the-river hydroelectric turbine powering the municipal grid." },
  { label: "Local Economy", value: "A regional barter-and-cooperative credit system that shields residents from global inflation." },
  { label: "Educational Anchor", value: "An active guild apprenticeship system pairing adolescents with veteran master artisans." },
  { label: "Seasonal Festival", value: "The Autumn Gate Gathering, where households taste the first pressed cider and test snow equipment." },
  { label: "Primary Transport", value: "A narrow-gauge diesel railcar connecting the valley farms to the coastal ferry quay." },
  { label: "Ecological Status", value: "Thriving temperate river basin with reintroduced beaver colonies and clean trout runs." },
  { label: "Cultural Habit", value: "Never closing a workshop door while a neighbor's lamp is still lit on the porch opposite." },
] as const;

// ==========================================
// 10. Dossier Banks: Skills, Motivations, Achievements, Failures, Dilemmas
// ==========================================
export const DOSSIER_SKILLS_BASE = [
  "Precision field surveying",
  "Analog hydraulic calibration",
  "Civic mediation and consensus building",
  "Hand-tool fabrication and repair",
  "Regional watershed analysis",
  "Heirloom materials conservation",
  "Emergency cold-weather logistics",
  "Deep-time oral recordkeeping",
  "Structural load-bearing assessment",
  "Apprentice pedagogy and technical training",
  "Microclimate barometric forecasting",
  "Cooperative ledger accounting",
] as const;

export const DOSSIER_MOTIVATIONS = [
  "Proving that human lives lived in deep continuity with place are as expansive as any metropolitan career.",
  "Preserving the tangible technical heritage of the valley so future generations never lose physical self-reliance.",
  "Sheltering neighbors from the volatile economic shocks of distant, speculative financial markets.",
  "Demonstrating that patience and physical honesty in craft outlast transient digital trends.",
  "Ensuring that vulnerable elders and arriving youth find an open seat at the community table.",
  "Holding the collective memory of what this landscape looked like before industrial homogenization.",
] as const;

export const DOSSIER_ACHIEVEMENTS = [
  "Engineered the gravity-fed canal bypass that protected the entire township from the historic 50-year flash flood.",
  "Negotiated a permanent 99-year communal conservation easement protecting 1,200 acres of old-growth ridge forest.",
  "Trained nineteen apprentices who now operate independent repair shops across six neighbouring districts.",
  "Restored the derelict municipal clock and tidal signal bells, which have chimed without failure for fourteen years.",
  "Co-founded the Valley Mutual Aid Society, which has prevented thirty-four family farm foreclosures.",
  "Cataloged over 800 hours of rare dialect audio recordings, now housed in the provincial library.",
] as const;

export const DOSSIER_FAILURES = [
  "Hesitated during the flood of their twenty-ninth year, resulting in the water-damage loss of three historic district map folios.",
  "Alienated an influential county planning board by bluntly refusing to certify an unsafe commercial extraction permit.",
  "Allowed pride to delay calling for regional assistance during an unseasonal frost, losing half the trial apple crop.",
  "Refused to speak to an estranged sibling for seven years over a disputed workshop boundary, a silence only broken at their funeral.",
  "Pushed a promising apprentice too hard during a rush contract, prompting the youth to quit and move to the capital.",
  "Failed to secure the grant funding needed to install the solar drying kiln before the supplier went out of business.",
] as const;

export const DOSSIER_DILEMMAS = [
  "Deciding whether to accept state funding to modernize the regional workshop with digital automation, knowing it will boost efficiency but make the traditional apprenticeship craft obsolete.",
  "Balancing the urgent duty to preserve the quiet, uncommercialized nature of the valley against the economic necessity of welcoming wealthy metropolitan tourism.",
  "Choosing between stepping into the contentious county council presidency to block predatory developers or staying at the drafting bench to complete their life's masterwork treatise.",
  "Navigating an offer from a national university to purchase and relocate their forty-year archive, which would bring global scholarship but strip the documents from the community that lived them.",
  "Wrestling with whether to urge their own gifted children to leave the valley for broader world opportunities or encourage them to carry on the family stewardship here.",
] as const;

export const DOSSIER_TRAJECTORY_NEAR = [
  "Over the coming two seasons, {name} will supervise the seismic retrofitting of the old stone weir while guiding three second-year apprentices through their final guild submissions.",
  "In the immediate months ahead, {name} plans to complete the definitive hydrological survey of the upper watershed before the autumn rains begin.",
  "The near horizon involves resolving a delicate boundary negotiation between upland timber contractors and the municipal water board, followed by publishing a pocket field guide.",
  "Over the next year, {name} is organizing the centenary restoration of the district community hall, coordinating volunteer labor from four surrounding villages.",
] as const;

export const DOSSIER_TRAJECTORY_LONG = [
  "Decades hence, when the tools are finally passed to younger hands, {name}'s enduring monument will be an intact, self-reliant valley that still knows how to repair what it breaks.",
  "In the long arc, {name} will be remembered as the steadfast custodian who held the line against mindless exploitation, leaving behind cleaner water, healthier soil, and a proud people.",
  "The eventual legacy will not be carved on grand public arches, but woven into the quiet, resilient daily habits of generations who learned from {name} that staying put is an act of love.",
  "Looking twenty years forward, {name}'s life work will form the bedrock of regional environmental law, proving that small communities can outlast the empires that overlook them.",
] as const;

// Geographic / Place names for realistic human settings across the globe
export const REGIONAL_HUMAN_PLACES = [
  { region: "the misty Atlantic coast", town: "Oakhaven", feature: "the granite breakwater" },
  { region: "the Mediterranean foothills", town: "Valle Verde", feature: "the terraced olive groves" },
  { region: "the Andean highlands", town: "Alturas del Valle", feature: "the glacial melt canal" },
  { region: "the Nordic archipelago", town: "Skogvik", feature: "the pine-crested fjord" },
  { region: "the West African savannah basin", town: "Osun River Valley", feature: "the red-clay riverbanks" },
  { region: "the East Asian river delta", town: "Qianling Port", feature: "the stone lock gates" },
  { region: "the South Asian plateau", town: "Deccan Foothills", feature: "the monsoon reservoir stepwells" },
  { region: "the British Isles western ridges", town: "Blackwood Tarn", feature: "the drystone fell wall" },
  { region: "the Pacific Northwest sound", town: "Cedar Inlet", feature: "the tidal timber booms" },
  { region: "the Central European plain", town: "Kowalski Mill", feature: "the willow-lined canal" },
  { region: "the Australian temperate hills", town: "Wattle Creek", feature: "the eucalyptus ridgeline" },
  { region: "the Hokkaido northern basin", town: "Asahidake Run", feature: "the volcanic hot springs weir" },
] as const;

export const DISTANT_METROPOLISES = [
  "the capital's steel-and-glass financial district",
  "a sprawling coastal metropolis of ten million strangers",
  "an overseas corporate consulting tower",
  "the bustling national administrative hub",
  "a competitive private technology campus overseas",
  "the glittering international trade port",
  "a prestigious metropolitan university district",
  "the high-speed commercial corridor of the eastern seaboard",
] as const;
